import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronRight, Layers } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import ChevronIcon from '@/component-library/primitives/Icons/ChevronIcon/ChevronIcon';
import { Workspace } from '@/models/WorkspacesByTenant';
import {
  selectCurrentWorkspace,
  selectWorkspaces,
  setSelectedWorkspace,
} from '@/modules/Dashboard/Workspaces/workspaceSlice';
import useOutsideClick from '@/modules/shared/hooks/useOutsideClick';
import { AppRoutes } from '@/utils/routes/router';

type WorkspacePickerVariant = 'standalone' | 'toolbar';

interface WorkspacePickerProps {
  variant?: WorkspacePickerVariant;
  className?: string;
}

/**
 * Workspace switcher: context list + link to manage all workspaces.
 * SaaS pattern reference: https://workos.com/docs/widgets/organization-switcher
 * Dropdown usability: https://www.nngroup.com/articles/drop-down-menus/
 */
const WorkspacePicker = ({ variant = 'standalone', className = '' }: WorkspacePickerProps) => {
  const { t } = useTranslation();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const workspaces = useAppSelector(selectWorkspaces);
  const dispatch = useAppDispatch();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

  const isToolbar = variant === 'toolbar';

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleSelectWorkspace = (next: Workspace) => {
    if (next.id === workspace?.id) {
      setIsDropdownOpen(false);
      return;
    }
    dispatch(setSelectedWorkspace(next));
    sessionStorage.setItem('workspace', JSON.stringify(next));

    const containsUUID = uuidRegex.test(location.pathname);

    if (containsUUID) {
      const parts = location.pathname.split('/');
      const firstPart = parts[1];
      window.location.assign('/' + firstPart);
    } else {
      navigate(0);
    }
  };

  useOutsideClick(dropdownRef, () => setIsDropdownOpen(false));

  const { pathname } = location;

  useEffect(() => {
    setIsDropdownOpen(false);
  }, [pathname]);

  const triggerClassName = isToolbar
    ? 'flex min-h-10 h-full w-full min-w-0 cursor-pointer select-none items-center gap-2 rounded-lg py-2 pl-3 pr-2 text-left [-webkit-tap-highlight-color:transparent] transition-colors hover:bg-custom-silver/40 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue/25'
    : 'flex w-full min-w-0 max-w-full cursor-pointer select-none items-center gap-2 rounded-lg border border-custom-lightSilver/80 bg-white px-3 py-2 text-left shadow-[0_1px_2px_rgba(1,3,11,0.03)] transition-colors duration-150 hover:border-custom-lightSilver hover:bg-custom-silver/20 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue/25';

  const rootClassName = [
    'relative min-w-0',
    // Toolbar: no flex-1 — otherwise the trigger stretches across the header when the parent passes max-w-full.
    isToolbar ? 'flex min-h-10 w-max max-w-[min(17rem,calc(100vw-9rem))] shrink-0' : 'inline-block max-w-[min(100%,17rem)]',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClassName} ref={dropdownRef}>
      <button
        type="button"
        onClick={toggleDropdown}
        className={`${triggerClassName} ${isToolbar && isDropdownOpen ? 'bg-custom-silver/35' : ''}`}
        aria-expanded={isDropdownOpen}
        aria-haspopup="listbox"
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-custom-silver/50 text-custom-darkSilver">
          <Layers className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
        </span>
        <span className="min-w-0 flex-1 overflow-hidden text-left">
          <span className="block truncate text-sm font-medium leading-snug text-custom-black" title={workspace?.name}>
            {workspace?.name ?? '—'}
          </span>
          <span className="mt-0.5 block truncate text-[11px] font-normal leading-tight text-custom-mediumSilver">
            {t('HeaderLoggedIn.workspaceLabel')}
          </span>
        </span>
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
            isDropdownOpen ? 'bg-custom-silver/70 text-custom-darkSilver' : 'bg-transparent text-custom-mediumSilver'
          } transition-colors`}
        >
          <ChevronIcon type={isDropdownOpen ? 'up' : 'down'} color="currentColor" width="12" height="7" />
        </span>
      </button>
      {isDropdownOpen && (
        <div
          className="absolute right-0 z-30 mt-1.5 w-[17rem] max-h-[min(28rem,70vh)] overflow-auto rounded-lg border border-custom-lightSilver/90 bg-white p-1.5 shadow-[0_4px_14px_rgba(1,3,11,0.06),0_1px_3px_rgba(1,3,11,0.04)]"
          role="listbox"
        >
          <p className="px-2 pb-1.5 pt-1 text-[10px] font-medium uppercase tracking-[0.1em] text-custom-mediumSilver">
            {t('WorkspacePicker.switchWorkspace')}
          </p>
          <ul className="space-y-0.5 py-0.5">
            {workspaces?.map((w) => {
              const selected = w.id === workspace?.id;
              return (
                <li key={w.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`flex w-full cursor-pointer flex-row items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors ${
                      selected
                        ? 'bg-custom-silver/55 font-medium text-custom-black'
                        : 'text-custom-black hover:bg-custom-silver/45'
                    }`}
                    onClick={() => handleSelectWorkspace(w)}
                  >
                    <span className="min-w-0 flex-1 truncate">{w.name}</span>
                    {selected && <Check className="h-4 w-4 shrink-0 text-custom-darkSilver" strokeWidth={2} aria-hidden />}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="mt-1.5 border-t border-custom-lightSilver/60 pt-1">
            <Link
              to={AppRoutes.workspaces}
              className="group flex items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-sm font-medium text-custom-black transition-colors hover:bg-custom-silver/45"
              onClick={() => setIsDropdownOpen(false)}
            >
              <span className="min-w-0 flex-1 truncate">{t('WorkspacePicker.manageAllWorkspaces')}</span>
              <ChevronRight
                className="h-4 w-4 shrink-0 text-custom-mediumSilver/80 transition-transform group-hover:translate-x-px group-hover:text-custom-darkSilver"
                strokeWidth={2}
                aria-hidden
              />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspacePicker;
